// Regras puras do editor de Mapas (sem Vue, sem DOM): formatos, ícones,
// setas, lista e moldura. Ficam aqui para serem testadas sem navegador.

export const COLORS = [
  '#2781f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

export const SHAPES = [
  'balloon',
  'card',
  'note',
  'text',
  'diamond',
  'pill',
  'frame',
];

// Tamanho inicial de quem precisa de caixa fixa (losango e moldura).
export const DEFAULT_SIZE = {
  diamond: { width: 200, height: 110 },
  frame: { width: 360, height: 240 },
};

// Ícones desenhados em SVG com traço (viewBox 24). A fonte de ícones do app
// NÃO renderiza dentro do template de nó do VueFlow — por isso o path fica aqui.
export const ICONS = {
  bot: 'M5 8h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zM12 4v4M9 14h.01M15 14h.01',
  user: 'M12 4a4 4 0 1 1 0 8a4 4 0 0 1 0-8zM4 21c1-4 4.5-6 8-6s7 2 8 6',
  calendar:
    'M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM8 3v4M16 3v4M3 10h18',
  chat: 'M4 5h16v11H9l-5 4zM8 10h.01M12 10h.01M16 10h.01',
  phone:
    'M5 4h4l2 5-3 2a11 11 0 0 0 5 5l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2',
  refresh:
    'M20 11a8 8 0 0 0-14-5l-2 2M4 13a8 8 0 0 0 14 5l2-2M4 4v4h4M20 20v-4h-4',
  document: 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6',
  trophy:
    'M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 21h8M10 17h4',
  close: 'M12 3a9 9 0 1 1 0 18a9 9 0 0 1 0-18zM9 9l6 6M15 9l-6 6',
  leaf: 'M5 19c0-9 6-14 15-14 0 9-5 15-14 15M5 19c3-3 6-6 10-9',
  megaphone: 'M3 10v4h4l7 5V5L7 10zM18 9a4 4 0 0 1 0 6',
  globe:
    'M12 3a9 9 0 1 1 0 18a9 9 0 0 1 0-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18',
  money: 'M3 7h18v10H3zM12 9a3 3 0 1 1 0 6a3 3 0 0 1 0-6zM6 10v4M18 10v4',
  star: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
  flag: 'M5 21V4M5 4h11l-2 4 2 4H5',
  check: 'M4 12l5 5L20 6',
};

export const ICON_KEYS = Object.keys(ICONS);

// Carregar mapa antigo não pode apagar o que o usuário configurou: antes o
// load recriava `data` só com texto/cor/formato e perdia fonte e trava.
export function normalizeNode(node) {
  const data = node.data || {};
  const shape = SHAPES.includes(data.shape) ? data.shape : 'balloon';
  const size = DEFAULT_SIZE[shape];
  const normalized = {
    ...node,
    type: shape === 'frame' ? 'frame' : 'idea',
    data: {
      ...data,
      label: data.label || '',
      color: data.color?.startsWith('#') ? data.color : COLORS[0],
      shape,
    },
  };
  if (size) {
    normalized.data.width = data.width || size.width;
    normalized.data.height = data.height || size.height;
  }
  // Moldura fica ATRÁS dos blocos que agrupa; quem deixa de ser moldura volta
  // para a camada normal.
  if (shape === 'frame') normalized.zIndex = -1;
  else if (normalized.zIndex === -1) delete normalized.zIndex;
  if (data.locked) normalized.draggable = false;
  return normalized;
}

// Bloco com lista: 1ª linha = título, demais = itens (marcadores "-" ou "•"
// digitados pelo usuário são tirados para não duplicar o marcador).
export function splitList(label) {
  const lines = String(label || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  const [title = '', ...rest] = lines;
  return {
    title,
    items: rest.map(item => item.replace(/^[-•*]\s*/, '')),
  };
}

// Opções de cada ligação (pedido Ivan: "escolher o tipo de ligação, traço,
// pontilhado, mais fino — várias formas").
export const LINE_TYPES = {
  curve: 'default',
  straight: 'straight',
  elbow: 'smoothstep',
  step: 'step',
};
export const LINE_PATTERNS = {
  solid: undefined,
  dashed: '8 6',
  dotted: '1 6',
};
export const LINE_WIDTHS = { thin: 1.5, medium: 2.5, thick: 4 };
export const ARROWS = ['none', 'end', 'both'];

// Formato antigo por linha: `dashed` (booleano) e `arrow` (booleano).
function patternOf(edgeData, globalDashed) {
  if (
    LINE_PATTERNS[edgeData.pattern] !== undefined ||
    edgeData.pattern === 'solid'
  ) {
    return edgeData.pattern;
  }
  if (typeof edgeData.dashed === 'boolean')
    return edgeData.dashed ? 'dashed' : 'solid';
  return globalDashed ? 'dashed' : 'solid';
}

function arrowOf(edgeData) {
  if (ARROWS.includes(edgeData.arrow)) return edgeData.arrow;
  return edgeData.arrow === false ? 'none' : 'end';
}

// Estilo da seta: tipo e tracejado GERAIS do mapa valem para quem não
// escolheu; cada linha pode ter tipo, traço, espessura, ponta, cor e rótulo.
export function edgeStyle({ color, lineType, globalDashed, edgeData = {} }) {
  const cor = edgeData.color || color;
  const pattern = patternOf(edgeData, globalDashed);
  const arrow = arrowOf(edgeData);
  const width = LINE_WIDTHS[edgeData.width] || LINE_WIDTHS.medium;
  const marker = { type: 'arrowclosed', color: cor, width: 18, height: 18 };
  return {
    type: LINE_TYPES[edgeData.lineType] || lineType,
    label: edgeData.label || undefined,
    markerEnd: arrow === 'none' ? undefined : marker,
    markerStart: arrow === 'both' ? marker : undefined,
    // `color` deixa o brilho do tema neon usar a cor da própria seta.
    style: {
      stroke: cor,
      color: cor,
      strokeWidth: width,
      strokeDasharray: LINE_PATTERNS[pattern],
      strokeLinecap: pattern === 'dotted' ? 'round' : undefined,
    },
    labelStyle: { fill: cor, fontWeight: 700, fontSize: 12 },
    labelBgStyle: { fill: 'var(--mapa-fundo)', stroke: cor, strokeWidth: 1.5 },
    labelBgPadding: [8, 4],
    labelBgBorderRadius: 8,
    data: { ...edgeData, pattern, arrow },
  };
}

// Valores efetivos da linha para a barra de edição (formato antigo incluso).
export function edgeOptions(edgeData = {}, globalDashed = false) {
  return {
    lineType: LINE_TYPES[edgeData.lineType] ? edgeData.lineType : null,
    pattern: patternOf(edgeData, globalDashed),
    width: LINE_WIDTHS[edgeData.width] ? edgeData.width : 'medium',
    arrow: arrowOf(edgeData),
    color: edgeData.color || null,
  };
}

// Quem está DENTRO da moldura acompanha o arrasto dela: o centro do bloco
// precisa cair na área da moldura.
export function nodesInsideFrame(frame, candidates, sizeOf) {
  const fx = frame.position.x;
  const fy = frame.position.y;
  const fw = frame.data.width;
  const fh = frame.data.height;
  return candidates
    .filter(node => node.id !== frame.id && node.data?.shape !== 'frame')
    .filter(node => {
      const { width = 0, height = 0 } = sizeOf(node) || {};
      const cx = node.position.x + width / 2;
      const cy = node.position.y + height / 2;
      return cx >= fx && cx <= fx + fw && cy >= fy && cy <= fy + fh;
    })
    .map(node => node.id);
}

// Tema do mapa: neon (lousa de vidro escura) ou claro.
export const THEMES = ['claro', 'neon'];

export function normalizeTheme(theme) {
  return THEMES.includes(theme) ? theme : 'claro';
}

// Soltar a seta em QUALQUER parte do bloco liga os dois: o lado de chegada é
// o que fica de frente para quem saiu (ninguém mira numa bolinha de 8px).
export function facingSide(sourceBox, targetBox) {
  const dx =
    targetBox.x + targetBox.width / 2 - (sourceBox.x + sourceBox.width / 2);
  const dy =
    targetBox.y + targetBox.height / 2 - (sourceBox.y + sourceBox.height / 2);
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'left' : 'right';
  return dy >= 0 ? 'top' : 'bottom';
}

// Pouco movimento entre apertar e soltar no "+" é CLIQUE (cria o próximo
// bloco); movimento maior é ARRASTO (puxa uma seta).
export function isClick(start, end, tolerance = 6) {
  if (!start || !end) return false;
  return Math.hypot(end.x - start.x, end.y - start.y) <= tolerance;
}
